import React from "react";
import { ChangeEvent, FormEvent, Fragment } from "react";
import ReactModal from "react-modal";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import OverflowMenuItem from "../OverflowMenuItem";
import TabControl from "../TabControl";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { V2 } from "../../Api";
import { formatDateAge, formatDateLocal, formatDateLocalDayOfWeek, formatDateLocalNumeric, formatName, formatPhone, formatTimeLocal, truncateEllipse } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";
import withAuth from "../withAuth";
import withTitle, { IViewTitleProps } from "../withTitle";

import "../../arrow.css";
import "../../blocks.css";
import "../../button.css";
import "../../schedule.css";
import "../viewer.css";

interface ISurgeryCalendarDayViewState {
    date: DateTime;
    events: { [time: string]: any[] };
    isLoading: boolean | string;
    list: any[];

    activity: any;

    locations: any[];
    searchStatus: string;
    searchLocation: string;
    searchType: string;

    viewMode: number;

    modalOpen: boolean;

    dialogCaseStatus: string;
    dialogCaseType: string;
    dialogPreset: string;

    [name: string]: any;
}

interface ISurgeryCalendarDayViewProps extends RouteComponentPropsHistory<any, any>, IViewTitleProps, IAuthProps {

}

class SurgeryCalendarDayView extends React.Component<ISurgeryCalendarDayViewProps, ISurgeryCalendarDayViewState> {
    private hasMessageHandler: boolean;

    constructor(props: Readonly<ISurgeryCalendarDayViewProps>) {
        super(props);

        const newDate = DateTime.fromISO(this.props.match.params.date);

        this.state = {
            date: newDate,
            events: {},
            isLoading: true,
            list: [],

            activity: {},

            locations: [],
            people: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchStatus: (props.location.state ? props.location.state.searchStatus : "") || "",
            searchType: (props.location.state ? props.location.state.searchType : "") || "",

            viewMode: (props.location.state ? props.location.state.viewMode : 0) || 0,

            modalOpen: false,

            dialogCaseStatus: "",
            dialogCaseType: "",
            dialogPreset: "not_office",
        }

        this.hasMessageHandler = false;

        this.prevDay = this.prevDay.bind(this);
        this.nextDay = this.nextDay.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.dialogInputChanged = this.dialogInputChanged.bind(this);
        this.showScheduleWindow = this.showScheduleWindow.bind(this);
        this.showScheduleDialog = this.showScheduleDialog.bind(this);
        this.handleMessage = this.handleMessage.bind(this);
        this.handleTabChange = this.handleTabChange.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.mapActivityRow = this.mapActivityRow.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await V2.get<any[]>("locations?type=surgery");

            this.setState({
                locations
            }, () => {
                if (this.state.viewMode === 2) {
                    this.loadActivity();
                }
                else {
                    this.requestEvents();
                }
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage);
    }

    public render() {
        let content;

        if (typeof this.state.isLoading === "string") {
            content = <div className="error-text">{this.state.isLoading}</div>;
        }
        else if (this.state.isLoading) {
            content = this.state.viewMode === 0
                ? <div className="lds-dual-ring spinner-center" />
                : <tr>
                    <td colSpan={20}>
                        <div className="lds-dual-ring spinner-center" />
                    </td>
                </tr>
        }
        else if (this.state.viewMode === 2) {
            content = [
                <tr key="_blankheader">
                    <td colSpan={20} />
                </tr>,
                <tr key="_movedfromday">
                    <th colSpan={20}>Moved from this day to a different day</th>
                </tr>,
                ...(this.state.activity.activityFromDay?.map(this.mapActivityRow, "from") || []),
                <tr key="_movedtoday">
                    <th colSpan={20}>Moved to this day</th>
                </tr>,
                ...(this.state.activity.activityToDay?.map(this.mapActivityRow, "to") || [])
            ];
        }
        else if (Object.keys(this.state.events).length === 0 && this.state.list.length === 0) {
            content = this.state.viewMode === 0
                ? <span>No appointments found</span>
                : <tr><td colSpan={20}>No appointments found</td></tr>;
        }
        else if (this.state.viewMode === 1) {
            content = this.state.list.map((a: any) => (
                <tr>
                    <td className="col-center">{a.time ? formatTimeLocal(DateTime.fromISO(`${a.date.substring(0, 10)}T${a.time}`, { zone: "America/New_York" }), true) : "N/A"}</td>
                    <td className="col-center">{this.appointmentTypeToString(a.appointmentType)}</td>
                    <td>{formatName(a.patient.name)}</td>
                    <td className="col-center">{formatDateAge(a.patient.birthDate)}</td>
                    <td className="wrap">{this.renderSurgeryList(a)}</td>
                    <td>{formatName(a.primarySurgeon.name)}</td>
                    <td className="col-center" data-caption="Anesthesia: ">{a.anesthesiaStartTime ? formatTimeLocal(a.anesthesiaStartTime, true) : "N/A"} &mdash; {a.anesthesiaEndTime ? formatTimeLocal(a.anesthesiaEndTime, true) : "N/A"}</td>
                    <td className="col-center" data-caption="Operation: ">{a.operationStartTime ? formatTimeLocal(a.operationStartTime, true) : "N/A"} &mdash; {a.operationEndTime ? formatTimeLocal(a.operationEndTime, true) : "N/A"}</td>
                    <td>
                        <Link to={`/surgery/${a.id}`} className="button small">View</Link>
                    </td>
                </tr>
            ));
        }
        else {
            content = Object.keys(this.state.events).map(k => (
                <section key={k} className="schedule-section">
                    <h3>
                        {
                            k === ""
                                ? "Not Set"
                                : formatTimeLocal(this.state.date.set({ hour: Math.floor(parseInt(k, 10) / 60), minute: Math.floor(parseInt(k, 10) % 60) }))
                        }
                    </h3>
                    <ul>
                        {
                            this.state.events[k].map((e: any) => (
                                <li key={e.id} className={this.statusToClass(e.status)}>
                                    <Link to={`/surgery/${e.id}`}>
                                        <span>{formatName(e.patient.name)} <span>(#{e.patient.chartNumber})</span> {e.hasDocuments ? <i className="icon-file-text-o" title="Has documents" /> : null}</span>
                                        <span className="small-text"><b>DoB: </b>{formatDateLocalNumeric(e.patient.birthDate)} ({formatDateAge(e.patient.birthDate)} y/o)<b>&nbsp;&nbsp;Tel: </b>{formatPhone(e.patient.phone||"")} <span className="print-only">&nbsp;&nbsp;<b>City: </b>{`${e.patient.address.city}, ${e.patient.address.state}`}</span></span>
                                        <span className="small-text member-list">{formatName(e.primarySurgeon.name)}</span>
                                        <span className="small-text"><b>Provider: </b>{e.provider ? formatName(e.provider.name) : "Not Specified"}</span>
                                        <span className="small-text"><b>Case Type: </b>{this.appointmentTypeToString(e.appointmentType)}<b>&nbsp;&nbsp;Insurance: </b>{e.patient.insuranceProvider} <span className="print-only">&nbsp;&nbsp;<b>Created: </b>{formatDateLocal(e.creationTimestamp)}</span></span>
                                        <HasRight right="surgery:read:m">
                                            <span className="small-text"><b>Marketer: </b>{e.marketer ? formatName(e.marketer.name) : "Not Specified"}<b>&nbsp;&nbsp;Type: </b>{e.bonus ? e.bonus.name : "Unset"}</span>
                                        </HasRight>
                                        <span className="small-text-dark">{this.renderSurgeryList(e)}</span>
                                        {e.note ? <span className="small-text-dark print-only"><b>Note: </b>{e.note}</span> : null}
                                        {e.notes ? e.notes.map((a: any, i: number) => <span key={i} className="small-text-dark print-only"><b>Note: </b>{a}</span>) : null}
                                    </Link>
                                </li>
                            ))
                        }
                    </ul>
                </section>
            ));
        }

        return (
            <div className="viewer-root">
                <ViewToolbar>
                    <HasRight right="appt:create-disabled">
                        <Link to={{ pathname: "/appointments/create", state: { date: this.state.date } }} className="icon-plus" />
                    </HasRight>
                    <ToolbarMenuItem>
                        <button type="button" className="icon-print" title="Print" onClick={this.printPage} />
                    </ToolbarMenuItem>
                    <OverflowMenuItem>
                        <ToolbarMenuItem>
                            <button type="button" onClick={this.showScheduleDialog}><i className="icon-print" /><span>Print schedule</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <ReactModal
                    isOpen={this.state.modalOpen} onRequestClose={this.closeDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Schedule print options">
                    <form className="input-form" onSubmit={this.showScheduleWindow}>
                        <section className="info-block">
                            <header>
                                <h3>Schedule Print Options</h3>
                            </header>

                            <span>Select what kind of schedule you want to print.</span>
                            <br />
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-dialog-presets">Preset</label>
                                    <select id="frm-dialog-presets" value={this.state.dialogPreset} name="dialogPreset" onChange={this.dialogInputChanged}>
                                        <option value="not_office">OR & Indigent schedule</option>
                                        <option value="office">Office schedule</option>
                                        <option value="all">All cases schedule</option>
                                        <option value="">Custom</option>
                                    </select>
                                </div>
                            </section>
                            {
                                this.state.dialogPreset === ""
                                    ?
                                    <Fragment>
                                        <br />
                                        <span>Select what kind of cases you want to be on the printout.</span>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-dialog-case-type">Case types</label>
                                                <select id="frm-dialog-case-type" value={this.state.dialogCaseType} name="dialogCaseType" onChange={this.dialogInputChanged}>
                                                    <option value="">All types</option>
                                                    <option value="!office">OR & Indigent</option>
                                                    <option value="office">Office only</option>
                                                    <option value="normal">OR only</option>
                                                    <option value="officeindigent">Indigent only</option>
                                                </select>
                                            </div>
                                        </section>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-dialog-status">Case status</label>
                                                <select id="frm-dialog-status" value={this.state.dialogCaseStatus} name="dialogCaseStatus" onChange={this.dialogInputChanged}>
                                                    <option value="">All</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="active">Active</option>
                                                    <option value="complete">Completed</option>
                                                    <option value="noshow">Soft-canceled</option>
                                                    <option value="canceled">Canceled</option>
                                                    <option value="notneeded">Canceled - Not Needed</option>
                                                </select>
                                            </div>
                                        </section>
                                    </Fragment>
                                    : ""
                            }
                            <br />
                            <div className="action-panel-dialog">
                                <button type="submit" className="button green push-right">Print</button>
                                <button type="button" className="button" onClick={this.closeDialog}>Cancel</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <div className="filter-control-box">
                    <div className="control-group">
                        <button className="button btn-light arrow arrow-left arrow-box" onClick={this.prevDay} />
                        <button className="button btn-light arrow arrow-right arrow-box" onClick={this.nextDay} />
                        <span>{formatDateLocalDayOfWeek(this.state.date)}</span>
                    </div>
                    <select className="push-right-if-wide" value={this.state.searchType} name="searchType" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All types</option>
                        <option value="!office">All but office</option>
                        <option value="normal">OR</option>
                        <option value="office">Office</option>
                        <option value="officeindigent">Indigent</option>
                    </select>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">Any Status</option>
                        <option value="canceled,noshow,notneeded">Any Canceled</option>
                        <option value="pending,active">Any Outstanding</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft Canceled</option>
                        <option value="canceled">Hard Canceled</option>
                        <option value="notneeded">Canceled - Not Needed</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                <TabControl headers={this.props.authContext.hasRight("surgery:history") ? ["Overview", "Details", "Activity"] : ["Overview", "Details"]} onTabChanged={this.handleTabChange} defaultSelectedIndex={this.state.viewMode}>
                    <div className="info-block">
                        <header>
                            <h3>Surgery Appointments</h3>
                        </header>
                        {content}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <table className="table-vertical">
                            <thead>
                                <tr>
                                    <th className="col-smallest col-center">Time</th>
                                    <th className="col-smallest col-center">Type</th>
                                    <th>Name</th>
                                    <th className="col-smallest col-center">Age</th>
                                    <th>Procedure(s)</th>
                                    <th>Surgeon</th>
                                    <th className="col-smallest col-center">Anesthesia</th>
                                    <th className="col-smallest col-center">Operation</th>
                                    <th className="col-smallest col-center" />
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Surgery Appointments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {content}
                            </tbody>
                        </table>
                    </div>
                    {
                        this.props.authContext.hasRight("surgery:history")
                            ? <div style={{ display: "flex", flexDirection: "column" }}>
                                <table className="table-vertical">
                                    <thead>
                                        <tr>
                                            <th className="col-smallest">Moved On</th>
                                            <th className="col-smallest">Employee</th>
                                            <th className="col-smallest" />
                                            <th className="col-smallest">From</th>
                                            <th className="col-smallest" />
                                            <th className="col-smallest">To</th>
                                            <th>Reason</th>
                                            <th className="col-smallest">Ordered On</th>
                                            <th className="col-smallest">Chart #</th>
                                            <th className="col-smallest">Patient</th>
                                            <th className="col-smallest" />
                                        </tr>
                                    </thead>
                                    <thead className="table-vertical-head-small">
                                        <tr>
                                            <th>Activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {content}
                                    </tbody>
                                </table>
                                <div className="color-key">
                                    <span className="color-key-entry"><span className="cell-white" /> Outstanding</span>
                                    <span className="color-key-entry"><span className="cell-green" /> Completed</span>
                                    <span className="color-key-entry"><span className="cell-yellow" /> Soft-canceled</span>
                                    <span className="color-key-entry"><span className="cell-orange" /> Canceled, not needed</span>
                                    <span className="color-key-entry"><span className="cell-red" /> Hard-canceled</span>
                                </div>
                            </div>
                            : null
                    }
                </TabControl>
            </div>
        );
    }

    private async requestEvents() {
        try {
            this.setState({
                isLoading: true
            });

            const start = this.state.date.startOf("day");
            const end = start.plus({ days: 1 });

            const parameters = [
                `after=${encodeURIComponent(start.toISO())}`,
                `before=${encodeURIComponent(end.toISO())}`,
                `mode=${this.state.viewMode === 0 ? "verbose" : "expanded"}`
            ];
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchType) {
                parameters.push(`type=${this.state.searchType}`);
            }

            const json = await V2.get<any>(`surgery/summary?${parameters.join("&")}`);

            const today = DateTime.local();
            if (start.hasSame(today, "day")) {
                this.props.viewTitleContext.changeTitle("Today's Surgery Calendar");
            }
            else {
                this.props.viewTitleContext.changeTitle("Surgery Calendar");
            }
            this.props.viewTitleContext.changeTitle(`Surgeries - ${formatDateLocalDayOfWeek(this.state.date)}`, "window");

            if (this.state.viewMode === 0) {
                // List
                const map: { [time: string]: any } = {};
                for (const event of json) {
                    let timeBin = "";
                    // HACK: For now, assume the time specified is in eastern time
                    event.timestamp = DateTime.fromISO(`${event.date.substring(0, 10)}${event.time ? `T${event.time}` : ""}`, { zone: "America/New_York" }).toLocal();
                    if (event.time) {
                        timeBin = (event.timestamp.hour * 60) + event.timestamp.minute;
                    }

                    if (!map[timeBin]) {
                        map[timeBin] = [];
                    }
                    map[timeBin].push(event);
                }

                // Sort Not Set events by creation date
                if (map[""]) {
                    map[""].sort((a: any, b: any) => a.creationTimestamp.localeCompare(b.creationTimestamp));
                }

                this.setState({
                    events: map,
                    isLoading: false,

                    activity: {},
                });
            }
            else {
                (json as any[]).forEach(v => {
                    if (v.anesthesiaStartTime) {
                        v.anesthesiaStartTime = DateTime.fromISO(`${v.date.substring(0, 10)}T${v.anesthesiaStartTime}`, { zone: "America/New_York" });
                    }
                    if (v.anesthesiaEndTime) {
                        v.anesthesiaEndTime = DateTime.fromISO(`${v.date.substring(0, 10)}T${v.anesthesiaEndTime}`, { zone: "America/New_York" });
                    }
                    if (v.operationStartTime) {
                        v.operationStartTime = DateTime.fromISO(`${v.date.substring(0, 10)}T${v.operationStartTime}`, { zone: "America/New_York" });
                    }
                    if (v.operationEndTime) {
                        v.operationEndTime = DateTime.fromISO(`${v.date.substring(0, 10)}T${v.operationEndTime}`, { zone: "America/New_York" });
                    }
                });

                // Table
                this.setState({
                    isLoading: false,
                    list: json.sort((a: any, b: any) => (a.time || "Z").localeCompare(b.time || "Z")),
                });
            }
        }
        catch (e) {
            // Request failed
            this.setState({
                isLoading: "Failed to load schedule"
            })
        }
    }

    private async loadActivity() {
        try {
            this.setState({
                isLoading: true
            });

            const start = this.state.date.startOf("day");

            const parameters = [
                `date=${encodeURIComponent(start.toISODate())}`,
            ];
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }

            const activity = await V2.get<any>(`surgery/activity?${parameters.join("&")}`);

            // Determine which ones have bounced through the day
            const toMap: { [id: string]: any } = {};
            for (const item of activity.activityToDay || []) {
                toMap[item.id] = item;
            }

            for (const item of activity.activityFromDay || []) {
                if (toMap[item.id]) {
                    toMap[item.id].isPass = true;
                    item.isPass = true;
                }
            }

            this.setState({
                isLoading: false,

                activity,
                events: {},
                list: []
            });
        }
        catch (e) {
            // Request failed
            this.setState({
                isLoading: e.message || "Failed to load activity"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 0: return "";
            case 1: return "";

            case 2: return "appt-bad";
            case 3: return "appt-noshow";
            case 4: return "appt-good";
            case 5: return "appt-disable";
            case 6: return "appt-disable";
            default: return "";
        }
    }

    private statusToRowColor(status: number) {
        switch (status) {
            case 6: return "row-gray";
            case 5: return "row-orange";
            case 4: return "row-green";
            case 3: return "row-yellow";
            case 2: return "row-red";
            case 1:
            case 0:
            default: return "";
        }
    }

    private prevDay() {
        // Yesterday
        this.gotoDate(this.state.date.minus({ days: 1 }));
    }

    private nextDay() {
        // Tomorrow
        this.gotoDate(this.state.date.plus({ days: 1 }));
    }

    private gotoDate(date: DateTime) {
        this.setState(s => {
            // Change date while keeping search state
            const currentLocation = this.props.location;
            currentLocation.pathname = `/surgery/calendar/${date.toISO().substr(0, 10)}`;
            this.props.history.replace(currentLocation);

            return {
                date
            }
        }, () => this.state.viewMode === 2 ? this.loadActivity() : this.requestEvents());
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            searchLocation: this.state.searchLocation,
            searchStatus: this.state.searchStatus,
            searchType: this.state.searchType,
            viewMode: this.state.viewMode,
        };
        this.props.history.replace(currentLocation);
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value,
            events: [],
        }, () => {
            this.saveSearchState();
            if (this.state.viewMode === 2) {
                this.loadActivity();
            }
            else {
                this.requestEvents();
            }
        });
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        if (e.currentTarget.type === "number") {
            this.setState({
                [name]: parseInt(value, 10)
            });
        }
        else {
            this.setState({
                [name]: value
            });
        }
    }

    private appointmentTypeToString(type: number) {
        switch (type) {
            case 0: return "OR";
            case 1: return "Office";
            case 2: return "Indigent";
            default: return `Unknown: ${type}`;
        }
    }

    private renderSurgeryList(surgery: any) {
        let items = [surgery.primarySurgery.shortName];
        if (surgery.primarySurgeryOptions && surgery.primarySurgeryOptions.length > 0) {
            items = items.concat(surgery.primarySurgeryOptions.map((b: any) => (b.glossaryOption && (b.glossaryOption.shortName || b.glossaryOption.fullName)) || b.id));
        }

        if (surgery.additionalSurgeries && surgery.additionalSurgeries.length > 0) {
            for (const item of surgery.additionalSurgeries) {
                items.push(item.surgery.shortName);
                for (const option of item.options) {
                    items.push((option.glossaryOption && (option.glossaryOption.shortName || option.glossaryOption.fullName)) || option.id)
                }
            }
        }

        return items.join(", ");
    }

    private showScheduleWindow(e: FormEvent) {
        e.preventDefault();

        this.ensureMessageHandler();

        this.setState({
            modalOpen: false
        });

        window.open("/print/surgery/schedule", "BOOK_WINDOW", "resizable,scrollbars");
    }

    private showScheduleDialog() {
        this.setState({
            modalOpen: true,

            dialogCaseStatus: "",
            dialogCaseType: "",
            dialogPreset: "all",
        });
    }

    private closeDialog() {
        this.setState({
            modalOpen: false,
        });
    }

    private handleMessage(e: MessageEvent) {
        if (!e.source || (e.source instanceof MessagePort) || (e.source instanceof ServiceWorker)) {
            return;
        }

        if (e.data.action && e.data.action === "surgery_schedule_request") {
            // Popup is asking for parameters

            const after = this.state.date;
            const before = this.state.date.endOf("day").plus({ millisecond: 1 });

            const message = {
                action: "surgery_schedule_parameters",
                after: after.toISO(),
                before: before.toISO(),
                location: this.state.searchLocation,
                status: this.state.searchStatus,
                type: this.state.searchType,
            };

            if (this.state.dialogPreset === "not_office") {
                message.status = "active";
                message.type = "!office";
            }
            else if (this.state.dialogPreset === "office") {
                message.status = "active";
                message.type = "office";
            }
            else if (this.state.dialogPreset === "all") {
                message.status = "active";
                message.type = ""; // All types
            }
            else {
                message.status = this.state.dialogCaseStatus;
                message.type = this.state.dialogCaseType;
            }

            e.source.postMessage(message, "*");
        }
    }

    private handleTabChange(index: number) {
        this.saveSearchState();

        if (index === 2) {
            // Activity
            this.setState({
                viewMode: index
            }, () => this.loadActivity());
            return;
        }

        this.setState({
            viewMode: index
        }, () => this.requestEvents());
    }

    private printPage() {
        window.print();
    }

    private ensureMessageHandler() {
        if (this.hasMessageHandler) {
            return;
        }

        window.addEventListener("message", this.handleMessage, false);
        this.hasMessageHandler = true;
    }

    private mapActivityRow(a: any, section: string) {
        return (
            <tr key={`${a.id}_${section || ""}`} className={this.statusToRowColor(a.status)}>
                <td className="inline" data-caption="Moved on: ">{formatDateLocalNumeric(a.timestamp)}</td>
                <td className="inline col-center" data-caption="Moved by: ">{formatName(a.employee.name)}</td>
                <td className="inline col-center" data-caption="Moved multiple times? ">{a.isPass ? <i title="Moved multiple times">&bull;</i> : null}</td>
                <td className="inline col-center" data-caption="From: ">{formatDateLocalNumeric(a.fromDate)}</td>
                <td className="inline col-center col-smallest"><i className="icon-long-arrow-right" /></td>
                <td className="inline col-center" data-caption="To: ">{formatDateLocalNumeric(a.targetDate)}</td>
                <td className="wrap" data-caption="Reason: ">{truncateEllipse(a.reason, 70)}</td>
                <td className="inline " data-caption="Ordered on: ">{formatDateLocalNumeric(a.orderedDate)}</td>
                <td className="inline col-right-vt" data-caption="Chart #: ">{a.chartNumber}</td>
                <td className="inline " data-caption="Name: ">{formatName(a.name)}</td>
                <td>
                    <Link to={`/surgery/${a.id}`} className="button small">View</Link>
                </td>
            </tr>
        );
    }
}

export default withAuth(withTitle("Surgery Calendar", SurgeryCalendarDayView));
